import React from 'react';
import type { Stats, TabId } from '../../types';
import { useLang } from '../../i18n/context';

interface SidebarProps {
  stats: Stats | null;
  onTabChange: (tab: TabId) => void;
  onCityClick?: (city: string | null) => void;
}

const LANG_LOCALE: Record<string, string> = { fr: 'fr-BE', en: 'en-GB', nl: 'nl-BE' };

export function Sidebar({ stats, onTabChange, onCityClick }: SidebarProps) {
  const { t, lang } = useLang();
  const locale = LANG_LOCALE[lang] || 'fr-BE';

  if (!stats) return null;

  const monthNames = t('date.months').split(',');
  const MONTH_NAMES: Record<string, string> = {
    '01': monthNames[0]?.slice(0, 3) || 'Jan',
    '02': monthNames[1]?.slice(0, 3) || 'Feb',
    '03': monthNames[2]?.slice(0, 3) || 'Mar',
    '04': monthNames[3]?.slice(0, 3) || 'Apr',
    '05': monthNames[4]?.slice(0, 3) || 'May',
    '06': monthNames[5]?.slice(0, 3) || 'Jun',
    '07': monthNames[6]?.slice(0, 3) || 'Jul',
    '08': monthNames[7]?.slice(0, 3) || 'Aug',
    '09': monthNames[8]?.slice(0, 3) || 'Sep',
    '10': monthNames[9]?.slice(0, 3) || 'Oct',
    '11': monthNames[10]?.slice(0, 3) || 'Nov',
    '12': monthNames[11]?.slice(0, 3) || 'Dec',
  };

  const maxMonthCount = Math.max(...(stats.by_month?.map(m => m.count) || [1]), 1);

  return (
    <aside className="sidebar">
      {/* This Week */}
      <div className="sidebar-widget">
        <div className="sidebar-widget-title">
          <span style={{ color: 'var(--blue)' }}>i</span> {t('sidebar.this_week')}
        </div>
        {stats.this_week.length === 0 ? (
          <div className="sidebar-empty">{t('sidebar.no_events')}</div>
        ) : (
          stats.this_week.map((event: any) => (
            <div key={event.id} className="sidebar-event-item">
              <div className="sidebar-event-date">
                {new Date(event.date).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })}
              </div>
              <div style={{ fontWeight: 600, fontSize: '13px' }}>{event.title}</div>
              {event.venue_name && (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{event.venue_name}</div>
              )}
            </div>
          ))
        )}
      </div>

      {/* By City */}
      <div className="sidebar-widget">
        <div className="sidebar-widget-title">
          <span style={{ color: 'var(--orange)' }}>&#127919;</span> {t('sidebar.by_city')}
        </div>
        <div className="city-list">
          <button className="city-chip active" onClick={() => onCityClick?.(null)}>
            {t('sidebar.all_cities')}
          </button>
          {stats.by_city?.map((c: any) => (
            <button
              key={c.city}
              className="city-chip"
              onClick={() => onCityClick?.(c.city)}
              title={t('sidebar.view_on_map', { n: c.city })}
            >
              {c.city} ({c.count})
            </button>
          ))}
        </div>
      </div>

      {/* By Month */}
      <div className="sidebar-widget">
        <div className="sidebar-widget-title">
          <span style={{ color: 'var(--green)' }}>&#128202;</span> {t('sidebar.by_month')}
        </div>
        {stats.by_month?.filter((m: any) => m.month).map((m: any) => {
          const monthLabel = MONTH_NAMES[m.month.split('-')[1]] || m.month;
          const pct = (m.count / maxMonthCount) * 100;
          return (
            <div key={m.month} className="month-bar">
              <span className="month-label">{monthLabel}</span>
              <div className="month-bar-fill" style={{ width: `${pct}%`, maxWidth: '150px' }} />
              <span className="month-count">{m.count}</span>
            </div>
          );
        })}
      </div>

      {/* Active Sources */}
      <div className="sidebar-widget">
        <div className="sidebar-widget-title">
          <span style={{ color: 'var(--green)' }}>&#128279;</span> {t('sidebar.active_sources')}
        </div>
        <div style={{ fontSize: '24px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
          {stats.active_sources}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
          {stats.total_artists} {t('artists.artists')} · {stats.total_sound_systems} {t('artists.sound_systems')} · {stats.total_venues} {t('sidebar.venues')}
        </div>
      </div>

    </aside>
  );
}
